import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Router } from "@angular/router";
import { BehaviorSubject, Observable, throwError, timer } from "rxjs";
import { catchError, retry, tap, map, switchMap } from "rxjs/operators";
import { apiEnvironment } from "@kells/clinic-one/environments";
import { DataAccessService } from "@kells/apis/data-access";
import * as Sentry from "@sentry/browser";

import {
  SSOResponse,
  SSOSuccessResponse,
  SSOErrorResponse,
  SSOAuthCodeExchangeRequest,
  SSOTokens,
  SSOError,
  SSOState,
  isSSOSuccessResponse,
  isSSOErrorResponse,
  isSSOTokenExchangeFailedResponse,
  SSO_ERROR_MESSAGES,
  SSO_STORAGE_KEYS,
} from "../models/sso.model";
import {
  QueryParameters,
  Portals,
} from "apps/patient-reporting/src/app/shared/enums/query.enums";
import { SubscriptionPlan } from "@kells/interfaces/patient";
import { getQueryParamsFromLink } from "apps/patient-reporting/src/app/shared/functions/get-query.functions";

/**
 * SSO Service
 * Handles Single Sign-On authentication flow including:
 * - Auth code exchange for tokens
 * - Token storage and management
 * - Integration with existing Auth0 system
 * - Error handling and user feedback
 */
@Injectable({
  providedIn: "root",
})
export class SSOService {
  private readonly ssoApiUrl = `${apiEnvironment.apiBaseUrl}/${apiEnvironment.apiVersion}/auth/sso/user/code/exchange`;

  // State management
  private ssoStateSubject = new BehaviorSubject<SSOState>({
    isLoading: true,
    error: null,
    isAuthenticated: false,
  });

  public ssoState$ = this.ssoStateSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    private dataAccess: DataAccessService
  ) {
    this.initializeAuthState();
  }

  /**
   * Persist redirect_to from the SSO landing URL before the async code exchange.
   * Angular's ActivatedRoute decodes query params correctly; window.location
   * parsing can miss URL-encoded paths like %2Fapp%2Fai-photo-scan.
   */
  setPostLoginRedirect(redirectTo: string): void {
    sessionStorage.setItem(SSO_STORAGE_KEYS.POST_LOGIN_REDIRECT, redirectTo);
  }

  /**
   * Exchange authentication code for tokens
   * @param authCode - The authentication code from SSO provider
   * @returns Observable<void> - Completes on success, errors on failure
   */
  exchangeAuthCode(authCode: string): Observable<void> {
    if (!authCode || authCode.trim().length === 0) {
      return throwError(
        this.createError("invalid_payload", "Authentication code is required")
      );
    }

    this.updateState({
      isLoading: true,
      error: null,
      isAuthenticated: false,
    });

    const request: SSOAuthCodeExchangeRequest = {
      auth_code: authCode.trim(),
    };

    return this.http.post<SSOResponse>(this.ssoApiUrl, request).pipe(
      switchMap((response: SSOResponse) => {
        if (isSSOSuccessResponse(response)) {
          return this.handleSuccessResponse(response);
        } else {
          return throwError(this.mapErrorResponse(response));
        }
      }),
      catchError((errorResponse: HttpErrorResponse) => {
        const ssoError = this.handleHttpError(errorResponse);
        this.updateState({
          isLoading: false,
          error: ssoError,
          isAuthenticated: false,
        });

        this.logErrorToSentry(ssoError, authCode);

        return throwError(ssoError);
      })
    );
  }

  /**
   * Handle successful token response
   * @param response - Success response with tokens
   * @returns Observable<void>
   */
  private handleSuccessResponse(
    response: SSOSuccessResponse
  ): Observable<void> {
    try {
      const tokens: SSOTokens = {
        ...response.data,
        issued_at: Date.now(),
      };

      this.storeTokens(tokens);
      this.updateState({
        isLoading: false,
        error: null,
        isAuthenticated: true,
      });

      return this.initializeUserSession(tokens);
    } catch (error) {
      const ssoError = this.createError(
        "token_exchange_failed",
        "Failed to process authentication tokens",
        undefined,
        "Token processing error"
      );

      this.updateState({
        isLoading: false,
        error: ssoError,
        isAuthenticated: false,
      });
      return throwError(ssoError);
    }
  }

  /**
   * Initialize user session after successful SSO authentication
   * @param tokens - SSO tokens
   * @returns Observable<void>
   */
  private initializeUserSession(tokens: SSOTokens): Observable<void> {
    return new Observable((observer) => {
      try {
        localStorage.setItem(SSO_STORAGE_KEYS.AUTH_STATE, "authenticated");
        localStorage.setItem(
          SSO_STORAGE_KEYS.LAST_LOGIN,
          Date.now().toString()
        );

        const userInfo = this.parseJwtPayload(tokens.id_token);

        if (userInfo && userInfo.email) {
          this.dataAccess.updateSelectedPatient({ email: userInfo.email });
          const queryParams = getQueryParamsFromLink(window.location.href);
          const portal = queryParams?.[QueryParameters.Portal];
          const plan = queryParams?.[QueryParameters.Plan];
          const redirectTo =
            this.consumePostLoginRedirect() ??
            queryParams?.[QueryParameters.RedirectTo];
          const navQueryParams: Partial<
            Record<QueryParameters, string | SubscriptionPlan>
          > = {};
          if (portal) {
            navQueryParams[QueryParameters.Portal] = portal;
          }
          if (plan) {
            navQueryParams[QueryParameters.Plan] = plan as SubscriptionPlan;
          }
          const destination = this.resolvePostLoginDestination(redirectTo);
          this.navigateAfterSsoLogin(destination, navQueryParams)
            .then(() => {
              observer.next();
              observer.complete();
            })
            .catch((error) => {
              observer.error(
                this.createError(
                  "unknown_error",
                  "Navigation failed after authentication",
                  undefined,
                  error.message
                )
              );
            });
        } else {
          observer.error(
            this.createError(
              "token_issue_failed",
              "Invalid user information in authentication token"
            )
          );
        }
      } catch (error) {
        observer.error(
          this.createError(
            "unknown_error",
            "Failed to initialize user session",
            undefined,
            error instanceof Error ? error.message : "Unknown error"
          )
        );
      }
    });
  }

  /**
   * Parse JWT payload to extract user information
   * @param token - JWT token
   * @returns User information object
   */
  private parseJwtPayload(token: string): any {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error("Failed to parse JWT payload:", error);
      return null;
    }
  }

  /**
   * Store tokens securely in localStorage
   * @param tokens - SSO tokens to store
   */
  private storeTokens(tokens: SSOTokens): void {
    try {
      const encryptedTokens = btoa(JSON.stringify(tokens));
      localStorage.setItem(SSO_STORAGE_KEYS.TOKENS, encryptedTokens);
    } catch (error) {
      console.error("Failed to store SSO tokens:", error);
      throw new Error("Token storage failed");
    }
  }

  /**
   * Retrieve stored tokens from localStorage
   * @returns SSO tokens or null if not found/invalid
   */
  public getStoredTokens(): SSOTokens | null {
    try {
      const encryptedTokens = localStorage.getItem(SSO_STORAGE_KEYS.TOKENS);
      if (!encryptedTokens) return null;

      const tokens: SSOTokens = JSON.parse(atob(encryptedTokens));

      // Check if tokens are expired
      const now = Date.now();
      const expirationTime = tokens.issued_at + tokens.expires_in * 1000;

      if (now >= expirationTime) {
        this.clearStoredTokens();
        return null;
      }

      return tokens;
    } catch (error) {
      console.error("Failed to retrieve SSO tokens:", error);
      this.clearStoredTokens();
      return null;
    }
  }

  /**
   * Clear stored tokens and auth state
   */
  public clearStoredTokens(): void {
    localStorage.removeItem(SSO_STORAGE_KEYS.TOKENS);
    localStorage.removeItem(SSO_STORAGE_KEYS.AUTH_STATE);
    localStorage.removeItem(SSO_STORAGE_KEYS.LAST_LOGIN);
  }

  /**
   * Check if user is authenticated via SSO
   * @returns boolean indicating authentication status
   */
  public isAuthenticated(): boolean {
    const tokens = this.getStoredTokens();
    const authState = localStorage.getItem(SSO_STORAGE_KEYS.AUTH_STATE);
    return !!(tokens && authState === "authenticated");
  }

  /**
   * Logout user and clear SSO state
   */
  public logout(): void {
    this.clearStoredTokens();
    this.updateState({
      isLoading: false,
      error: null,
      isAuthenticated: false,
    });
  }

  /**
   * Initialize authentication state on service creation
   */
  private initializeAuthState(): void {
    const isAuth = this.isAuthenticated();
    this.updateState({
      isLoading: false,
      error: null,
      isAuthenticated: isAuth,
    });
  }

  /**
   * Map API error response to user-friendly error
   * @param response - Error response from API
   * @returns SSOError object
   */
  private mapErrorResponse(response: SSOErrorResponse): SSOError {
    const message =
      SSO_ERROR_MESSAGES[response.message as keyof typeof SSO_ERROR_MESSAGES] ||
      SSO_ERROR_MESSAGES.unknown_error;

    const error: SSOError = {
      message,
      technical: response.message,
    };

    // Add request ID for certain error types
    if (isSSOTokenExchangeFailedResponse(response)) {
      error.requestId = response.data.request_id;
    }

    return error;
  }

  /**
   * Handle HTTP errors and convert to SSOError
   * @param error - HTTP error or existing SSOError
   * @returns SSOError object
   */
  private handleHttpError(errorResponse: HttpErrorResponse): SSOError {
    const error = errorResponse.error;
    if (error && isSSOErrorResponse(error)) {
      return this.mapErrorResponse(error);
    }
    if (errorResponse.status === 0) {
      return this.createError(
        "network_error",
        SSO_ERROR_MESSAGES.network_error
      );
    }

    return this.createError(
      "unknown_error",
      SSO_ERROR_MESSAGES.unknown_error,
      undefined,
      errorResponse.message
    );
  }

  /**
   * Create standardized error object
   * @param type - Error type key
   * @param message - User-friendly message
   * @param requestId - Optional request ID
   * @param technical - Optional technical details
   * @returns SSOError object
   */
  private createError(
    type: keyof typeof SSO_ERROR_MESSAGES,
    message?: string,
    requestId?: string,
    technical?: string
  ): SSOError {
    return {
      message: message || SSO_ERROR_MESSAGES[type],
      requestId,
      technical: technical || type,
    };
  }

  /**
   * Resolve the post-login navigation destination from the redirect_to param.
   *
   * Only paths that start with /app/ (internal app routes) are accepted.
   * Anything else — external URLs, open redirects, or unknown paths — falls
   * back to the default /app home.
   */
  private resolvePostLoginDestination(redirectTo?: string): string {
    const DEFAULT = "/app";
    if (!redirectTo) return DEFAULT;

    let path = redirectTo.trim();
    try {
      path = decodeURIComponent(path);
    } catch {
      // keep raw value if decoding fails
    }
    if (!path.startsWith("/")) {
      path = `/${path}`;
    }
    if (path.includes("://") || !path.startsWith("/app")) {
      return DEFAULT;
    }
    return path;
  }

  private consumePostLoginRedirect(): string | undefined {
    const raw = sessionStorage.getItem(SSO_STORAGE_KEYS.POST_LOGIN_REDIRECT);
    sessionStorage.removeItem(SSO_STORAGE_KEYS.POST_LOGIN_REDIRECT);
    return raw ?? undefined;
  }

  private navigateAfterSsoLogin(
    destination: string,
    navQueryParams: Partial<Record<QueryParameters, string | SubscriptionPlan>>
  ): Promise<boolean> {
    const params = new URLSearchParams();
    if (navQueryParams[QueryParameters.Portal]) {
      params.set(
        QueryParameters.Portal,
        String(navQueryParams[QueryParameters.Portal])
      );
    }
    if (navQueryParams[QueryParameters.Plan]) {
      params.set(
        QueryParameters.Plan,
        String(navQueryParams[QueryParameters.Plan])
      );
    }
    const qs = params.toString();
    const url = qs ? `${destination}?${qs}` : destination;
    return this.router.navigateByUrl(url, { replaceUrl: true });
  }

  /**
   * Update SSO state
   * @param partialState - Partial state to update
   */
  private updateState(partialState: Partial<SSOState>): void {
    const currentState = this.ssoStateSubject.value;
    this.ssoStateSubject.next({ ...currentState, ...partialState });
  }

  /**
   * Log error to Sentry for monitoring
   * @param error - SSO error
   * @param authCode - Auth code (masked for security)
   */
  private logErrorToSentry(error: SSOError, authCode?: string): void {
    try {
      Sentry.withScope((scope) => {
        scope.setTag("service", "sso");
        scope.setTag("error_type", error.technical || "unknown");
        scope.setContext("sso_error", {
          message: error.message,
          requestId: error.requestId,
          technical: error.technical,
          authCodeLength: authCode ? authCode.length : 0,
          timestamp: new Date().toISOString(),
        });

        Sentry.captureException(new Error(`SSO Error: ${error.message}`));
      });
    } catch (sentryError) {
      console.error("Failed to log error to Sentry:", sentryError);
    }
  }
}
